import React, { useMemo } from 'react'

// Utils
import { remCalc, concatStyles, useBreakpoint } from 'utils'

import { Indicators } from '../..'
import { NavigatorContext, NavigatorTheme } from './NavigatorContext'

const DRAWER_VIEW_STYLES =
  'flex flex-col flex-grow flex-shrink-0 text-navcontent w-full overflow-y-auto pb-0 xl:max-w-xs left-0'

const PRIMARY_STYLES = 'z-10 bg-navbackground '
const SECONDARY_STYLES = 'bg-navactive pb-32 z-20 hidden md:flex'

const DRAWER_VIEW_INLINE_STYLES = {
  height: `calc(100vh - ${remCalc(64)})`,
}

const DRAWER_ITEM_LIST =
  'list-none flex flex-col flex-grow p-0 m-0 flex-shrink-0'

type MaybeElement = JSX.Element | null

export interface IDrawerProps {
  compact?: boolean
  hasCompactSibling?: boolean
  hasSibling?: boolean
  theme?: NavigatorTheme
  children: JSX.Element | MaybeElement[]
  push?: boolean
  relative?: boolean
  loading?: boolean
  name?: string
}

/**
 * The primary navigation drawer
 * @param props Child components and layout configuration
 */
export const Drawer: React.FC<IDrawerProps> = (props) => {
  const viewPorts = useBreakpoint()
  const minify = useMemo(
    () =>
      (!viewPorts.includes('lg') &&
        viewPorts.includes('md') &&
        props.compact) ||
      false,
    [viewPorts, props.compact]
  )
  return (
    <NavigatorContext.Provider
      value={{
        compact: minify,
        theme: props.theme || 'primary',
      }}
    >
      <nav
        style={
          props.relative
            ? { position: 'relative' }
            : {
                ...DRAWER_VIEW_INLINE_STYLES,
                position: 'sticky',
                top: 0,
              }
        }
        className={concatStyles(
          DRAWER_VIEW_STYLES,
          props.theme === 'secondary' ? SECONDARY_STYLES : PRIMARY_STYLES,
          minify ? 'md:w-16' : 'md:w-64'
        )}
      >
        {props.loading ? (
          <Indicators.AbsoluteOverlay>
            <Indicators.Spinner size="3x" />
          </Indicators.AbsoluteOverlay>
        ) : null}
        <ul className={concatStyles(DRAWER_ITEM_LIST, props.push && 'pt-16')}>
          {React.Children.toArray(props.children).filter((c) => c)}
        </ul>
      </nav>
    </NavigatorContext.Provider>
  )
}
